import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TeamLeaveCalendarView = ({ 
  calendarData, 
  currentYear, 
  currentMonth, 
  onPrevMonth, 
  onNextMonth 
}) => {

  const getDaysInMonth = (year, month) => {
    const date = new Date(year, month - 1, 1);
    const result = [];
    const firstDayIndex = (date.getDay() + 6) % 7;
    for (let i = 0; i < firstDayIndex; i++) {
      result.push(null);
    }
    while (date.getMonth() === month - 1) {
      result.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return result;
  };

  const getMonthNames = () => [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const formatDateStr = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const absencesMap = useMemo(() => {
    const map = {};
    if (calendarData && calendarData.employees) {
      calendarData.employees.forEach(emp => {
        emp.leaves.forEach(leave => {
          if (leave.status === 'approved') {
            const start = leave.start_datetime ? leave.start_datetime.slice(0, 10) : leave.start_date;
            const end = leave.end_datetime ? leave.end_datetime.slice(0, 10) : leave.end_date;
            
            // Populate all days in the range
            let current = new Date(`${start}T00:00:00Z`);
            const endDate = new Date(`${end}T00:00:00Z`);
            const isReturnConvention = leave.end_datetime && /T00:00:00/.test(leave.end_datetime);
            
            if (isReturnConvention) {
              // Exclude return date (endDate)
              endDate.setDate(endDate.getDate() - 1);
            }
            
            while (current <= endDate) {
              const dStr = formatDateStr(current);
              if (!map[dStr]) map[dStr] = [];
              // Prevent duplicates if multiple leaves overlap for same user
              if (!map[dStr].find(a => a.id === emp.user_id)) {
                 map[dStr].push({ 
                   id: emp.user_id, 
                   name: emp.user_name || 'Inconnu', 
                   type: leave.leave_type 
                 });
              }
              current.setDate(current.getDate() + 1);
            }
          }
        });
      });
    }
    return map;
  }, [calendarData]);

  const days = getDaysInMonth(currentYear, currentMonth);
  const monthName = getMonthNames()[currentMonth - 1];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
         <div>
            <h3 className="text-xl font-bold text-gray-800">Calendrier des Absences</h3>
            <div className="text-sm text-gray-500 mt-1">Vue globale de l'équipe (Approuvées)</div>
         </div>
         <div className="flex items-center gap-4">
            <div className="text-lg font-bold text-[#1A1B4B] w-32 text-center">
              {monthName} {currentYear}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onPrevMonth} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 shadow-sm">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={onNextMonth} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 shadow-sm">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
         </div>
      </div>

      <div className="overflow-x-auto pb-4 custom-scrollbar">
        <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden min-w-[300px] lg:min-w-[700px]">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d, i) => (
            <div key={i} className="bg-gray-50 py-2 md:py-3 text-center text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wider">
              <span className="md:hidden">{d.charAt(0)}</span>
              <span className="hidden md:inline">{d}</span>
            </div>
          ))}
        {days.map((date, idx) => {
          if (!date) return <div key={`empty-${idx}`} className="bg-white min-h-[60px] md:min-h-[100px]" />;
          
          const dateStr = formatDateStr(date);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const dayAbsences = absencesMap[dateStr] || [];
          
          return (
            <div key={dateStr} className={`bg-white min-h-[60px] md:min-h-[100px] p-1 md:p-2 flex flex-col transition-colors hover:bg-gray-50 ${isWeekend ? 'bg-gray-50/50' : ''}`}>
               <div className={`text-xs md:text-sm font-bold mb-1 md:mb-2 text-center md:text-left md:ml-1 ${isWeekend ? 'text-gray-400' : 'text-gray-700'}`}>
                 {date.getDate()}
               </div>
               <div className="flex flex-row md:flex-col flex-wrap md:flex-nowrap justify-center md:justify-start gap-1 md:gap-1.5 flex-1 max-h-[80px] overflow-y-auto custom-scrollbar md:pr-1">
                 {dayAbsences.map(abs => {
                    const isSick = abs.type === 'sick_leave';
                    const isComp = abs.type === 'compensatory';
                    const isAcci = abs.type === 'work_accident';
                    const isUnju = abs.type === 'unjustified';
                    
                    const boxClass = isSick ? 'bg-orange-50 text-orange-700 border-orange-200' : 
                                     isComp ? 'bg-purple-50 text-purple-700 border-purple-200' : 
                                     isAcci ? 'bg-red-50 text-red-700 border-red-200' :
                                     isUnju ? 'bg-slate-50 text-slate-700 border-slate-200' :
                                     'bg-emerald-50 text-emerald-700 border-emerald-200';


                    const initials = abs.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                    
                    return (
                      <div 
                        key={abs.id} 
                        title={`${abs.name} (${abs.type})`}
                        className={`flex text-xs md:px-2 md:py-1 rounded border-0 md:border shadow-none md:shadow-sm font-semibold truncate ${boxClass.replace(/bg-.*50 /,'').replace(/border-.*200/,'')}`}
                      >
                         <div className={`hidden md:block truncate w-full ${boxClass}`}>{abs.name.split(' ')[0]}</div>
                         <div className={`md:hidden flex items-center justify-center w-5 h-5 rounded-full border text-[9px] ${boxClass}`}>{initials}</div>
                      </div>
                    );
                 })}
               </div>
            </div>
          );
        })}
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-xs font-semibold text-gray-600">
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200"></div> Congés Payés</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-orange-50 border border-orange-200"></div> Maladie</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-50 border border-red-200"></div> Accident de travail</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-50 border border-purple-200"></div> Récupération</span>
        <span className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-slate-50 border border-slate-200"></div> Absence injustifiée</span>
      </div>
    </div>
  );
};

export default TeamLeaveCalendarView;
